import '../../lib/polyfill';
import * as React from 'react';
import { NextPage } from 'next';
import Head from 'next/head';
import {
  ExperienceHostEnum,
  Product,
} from '@narvar/nth-kit-dynamic-track-page';
import { NoflakeTag } from '@narvar/nth-hook-context-analytics';
import { NOFLAKE_URL } from '../../constants/env';
import PageComponent from '../../components/PageComponent';
import PostMessageComponent from '../../components/PostMessageComponent';
import errorCallback from '../../lib/errorCallback';
import { PageProps } from './types';
import useApolloClientBrowser from './apolloClientBrowser';

const Page: NextPage<PageProps> = ({
  bannerName,
  bannerSegmentId,
  bannerSegmentName,
  cache,
  carrier,
  category,
  serverClient,
  destinationCountry,
  destinationZip,
  email,
  experienceHost,
  embedReferrerUrl,
  renderHeader,
  initialState,
  itemIds,
  itemNumber,
  localeCountry,
  localeLanguage,
  mainShipmentId,
  requestUrl,
  orderDate,
  orderNumbers,
  originCountry,
  originZip,
  phone,
  previewDate,
  product,
  productCategory,
  promiseDate,
  retailerDcId,
  retailerMoniker,
  segmentId,
  segmentName,
  service,
  shipDate,
  title,
  trackingNumbers,
  trackingType,
  type,
  unsupportedParams,
  userAgentDevice,
  versionName,
  token,
  skipAnalytics = false,
  growthbook,
  titanAnalyticsOptions,
  onUnauthenticatedRequest,
  claimPortalUrl,
  narvarProfileUrl,
}): React.ReactElement | null => {
  // This will only give us a NarvarApolloClient in the browser
  const apolloClientBrowser = useApolloClientBrowser({
    initialState,
  });

  React.useEffect(() => {
    if (apolloClientBrowser) apolloClientBrowser.startCacheTtlChecker();
  }, []);

  // `serverClient` will be undefined when run in the browser.
  // `client` can logically be null with the below code, but
  // it realistically won't ever be. We'll always have `serverClient`
  // passed in on the server, or the browser client when running
  // in the browser. So, `null` won't ever be returned. It's just an
  // exhaustive check because `ApolloProvider` enforces the `client`
  // prop is defined.
  const client = serverClient || apolloClientBrowser?.client;
  if (!client) {
    throw new Error(
      'Expected an ApolloClient instance to be available during page render, but one was not provided.',
    );
  }

  return (
    <>
      {experienceHost === ExperienceHostEnum.EMBED && <PostMessageComponent />}
      <Head>
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <title>{title}</title>
        <link
          title="favicon"
          rel="shortcut icon"
          type="image/png"
          href="https://assets.narvar.com/retailers/general/favicon.png"
        />
      </Head>
      <PageComponent
        userAgentDevice={userAgentDevice}
        renderHeader={renderHeader || experienceHost !== 'EMBED'}
        apollo={{ client }}
        initialState={{
          bannerName,
          bannerSegmentId,
          bannerSegmentName,
          cache,
          carrier,
          category,
          destinationCountry,
          destinationZip,
          email,
          experienceHost,
          embedReferrerUrl,
          itemIds,
          itemNumber,
          localeCountry,
          localeLanguage,
          mainShipmentId,
          requestUrl,
          orderDate,
          orderNumbers,
          originCountry,
          originZip,
          phone,
          previewDate,
          product: product || Product.narvarjs,
          productCategory,
          promiseDate,
          retailerDcId,
          retailerMoniker: retailerMoniker || '',
          segmentId,
          segmentName,
          service,
          shipDate,
          trackingNumbers,
          trackingType,
          type,
          unsupportedParams,
          versionName: versionName || '',
          token,
          claimPortalUrl,
          narvarProfileUrl,
        }}
        analytics={{
          noflakeUrl: NOFLAKE_URL,
          errorCallback,
          data: {
            tag: NoflakeTag.COMPONENT_PLATFORM,
            eventSchema: NoflakeTag.COMPONENT_PLATFORM,
            productName: product,
            pageRetailerMoniker: retailerMoniker,
          },
          skip: skipAnalytics,
        }}
        growthbook={growthbook}
        titanAnalyticsOptions={titanAnalyticsOptions}
        onUnauthenticatedRequest={onUnauthenticatedRequest}
      />
    </>
  );
};

if (typeof window === 'undefined') {
  Page.getInitialProps = async (context): Promise<PageProps> => {
    try {
      const { default: getInitialProps } = await import('./getInitialProps');

      return getInitialProps(context);
    } catch (err) {
      const graphQLErrors =
        typeof err === 'object' && err !== null && 'graphQLErrors' in err
          ? err.graphQLErrors
          : undefined;
      if (!graphQLErrors) {
        const { default: logger } = await import('../../lib/logger');
        logger.error(err);
      }

      // NOTE: When `NODE_ENV=production`, this will show the error page.
      // Go to `/_error` to view the error page.
      throw err;
    }
  };
}

export default Page;
